import type { ModuleStructure, Row } from './index.h'

import { api } from '@setplex/tria-api'
import { createEffect, sample } from 'effector'
import { isType } from 'variant'
import { getOnlyFavorites } from '../../ui/lib/style'
import { patchCachedStructures } from './cache'
import { Ready } from './index.h'
import { $structure } from './model'

export type FavoriteContentType = 'TV_CHANNEL' | 'VOD' | 'TV_SHOW'

type FavoriteItem = { id: number }

const FAVORITE_ROW_TITLE_KEYS: Record<FavoriteContentType, string> = {
  TV_CHANNEL: 'channel_favorites',
  VOD: 'movies_favorite',
  TV_SHOW: 'series_favorite',
}

// "only-favorites" rows are recognized either by their well-known
// localizedTitleKey (client-built defaults) or by the `only-favorites=true`
// query param on the row resource (backend-configured rows).
const isOnlyFavoritesRow = (row: Row, contentType: FavoriteContentType) => {
  if (row.desc.contentType !== contentType) return false

  if (row.desc.localizedTitleKey === FAVORITE_ROW_TITLE_KEYS[contentType]) {
    return true
  }

  return getOnlyFavorites(new URLSearchParams(row.desc.resource?.search))
}

const getReadyPayload = (
  row: Row,
  contentType: FavoriteContentType
): FavoriteItem[] | null => {
  if (row.desc.contentType !== contentType) return null
  if (!isType(row.data, Ready)) return null

  const payload = row.data.payload
  return Array.isArray(payload) ? payload : null
}

// Find the full entity for `id` in any already-loaded row of the same content
// type on the current page. Used to source the item being inserted into a
// favourites row, since the favorite-toggle response itself carries no entity.
const findLoadedItem = (
  structure: ModuleStructure,
  contentType: FavoriteContentType,
  id: number
): FavoriteItem | null => {
  for (const row of structure.values()) {
    const payload = getReadyPayload(row, contentType)
    const found = payload?.find((item) => item?.id === id)
    if (found) return found
  }

  return null
}

// Patch every loaded "only-favorites" row of `contentType` in `structure` to
// reflect a successful add/remove: insert the item (if found elsewhere on the
// page) or remove it by id. Returns the same `structure` reference when
// nothing changed, so effector skips the store update.
export const syncFavoriteRowsOnStructure = (
  structure: ModuleStructure,
  contentType: FavoriteContentType,
  id: number,
  favorite: boolean
): ModuleStructure => {
  let updated: ModuleStructure | null = null

  for (const [key, row] of structure) {
    if (!isOnlyFavoritesRow(row, contentType)) continue

    const payload = getReadyPayload(row, contentType)
    if (!payload) continue

    const hasItem = payload.some((item) => item?.id === id)
    if (favorite === hasItem) continue

    let nextPayload: FavoriteItem[]

    if (favorite) {
      const item = findLoadedItem(structure, contentType, id)
      // Item isn't loaded anywhere on the page yet - nothing to insert;
      // the row will pick it up on its next natural reload.
      if (!item) continue
      nextPayload = [item, ...payload]
    } else {
      nextPayload = payload.filter((item) => item?.id !== id)
    }

    if (!updated) updated = new Map(structure)
    updated.set(key, { ...row, data: Ready(nextPayload) })
  }

  return updated ?? structure
}

// The toggle is often pressed on another page than the favourite row, e.g. on
// movie details reached from Home, so the modules kept in the cache have to be
// patched too - otherwise the stale row is restored on the way back.
const syncCachedFavoriteRowsFx = createEffect(
  ({
    contentType,
    id,
    favorite,
  }: {
    contentType: FavoriteContentType
    id: number
    favorite: boolean
  }) => {
    patchCachedStructures((structure) =>
      syncFavoriteRowsOnStructure(structure, contentType, id, favorite)
    )
  }
)

for (const [effectFx, contentType] of [
  [api.tv.updateTvChannelFx, 'TV_CHANNEL'],
  [api.vods.updateVodFx, 'VOD'],
  [api.tvshows.updateTvShowFx, 'TV_SHOW'],
] as const) {
  sample({
    clock: effectFx.done,
    source: $structure,
    filter: (structure, { params }) =>
      Boolean(structure) && typeof params.favorite === 'boolean',
    fn: (structure, { params }) =>
      syncFavoriteRowsOnStructure(
        structure!,
        contentType,
        params.id,
        params.favorite!
      ),
    target: $structure,
  })

  sample({
    clock: effectFx.done,
    filter: ({ params }) => typeof params.favorite === 'boolean',
    fn: ({ params }) => ({
      contentType,
      id: params.id,
      favorite: params.favorite!,
    }),
    target: syncCachedFavoriteRowsFx,
  })
}
